import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadDeliveryNoteByIdUrl,
  zadDeliveryNoteBySaleUrl,
  zadDeliveryNotesUrl,
} from '../../api'
import {parseZadApiError} from '../zad/errors'
import {sanitizeZadDeliveryNoteWritePayload} from '../zad/deliveryNoteHelpers'
import {
  CreateZadDeliveryNoteRequest,
  FetchZadDeliveryNoteByIdRequest,
  FetchZadDeliveryNoteBySaleRequest,
} from './types'

function isMissingDeliveryNote(error: unknown): boolean {
  const parsed = parseZadApiError(error)
  return parsed.errorCode === 'DELIVERY_NOTE_NOT_FOUND' || parsed.status === 404
}

export const fetchZadDeliveryNoteBySale = createAsyncThunk(
  'zadDeliveryNotes/fetchBySale',
  async (payload: FetchZadDeliveryNoteBySaleRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadDeliveryNoteBySaleUrl(payload.saleId))
      return {
        success: true as const,
        deliveryNote: res.data.deliveryNote || null,
        missing: false,
      }
    } catch (error) {
      if (isMissingDeliveryNote(error)) {
        return {
          success: true as const,
          deliveryNote: null,
          missing: true,
        }
      }
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadDeliveryNoteById = createAsyncThunk(
  'zadDeliveryNotes/fetchById',
  async (payload: FetchZadDeliveryNoteByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadDeliveryNoteByIdUrl(payload.id))
      return {
        success: true as const,
        deliveryNote: res.data.deliveryNote,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadDeliveryNote = createAsyncThunk(
  'zadDeliveryNotes/create',
  async (payload: CreateZadDeliveryNoteRequest, {rejectWithValue}) => {
    const body = sanitizeZadDeliveryNoteWritePayload(payload)
    try {
      const res = await axios.post(zadDeliveryNotesUrl, body)
      return {
        success: true as const,
        deliveryNote: res.data.deliveryNote,
        recovered: false,
      }
    } catch (error) {
      const parsed = parseZadApiError(error)
      if (parsed.errorCode === 'DELIVERY_NOTE_ALREADY_EXISTS') {
        try {
          const existing = await axios.get(zadDeliveryNoteBySaleUrl(body.sale))
          if (existing.data.deliveryNote) {
            return {
              success: true as const,
              deliveryNote: existing.data.deliveryNote,
              recovered: true,
            }
          }
        } catch {
          /* fall through */
        }
      }
      return rejectWithValue(parsed)
    }
  }
)
